// Number, money and date formatting (P1-E07): «1 236», «$1 284», «$0,91», «4 165 000 сум», «128,4 млн сум»,
// «01.03.26 – 28.02.27», «12 мин», «вчера». Locale-aware through the active i18n language; RU and UZ share
// the grouping (no-break space) and the decimal comma.
import i18n from '@/i18n';

const TZ = 'Asia/Tashkent';

function locale(): string {
  return i18n.language === 'uz' ? 'uz-Latn-UZ' : 'ru-RU';
}

/** 1236 → «1 236» (no-break space grouping). */
export function formatNumber(n: number, fractionDigits = 0): string {
  return new Intl.NumberFormat(locale(), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(n);
}

/** Sums: 4165000 → «4 165 000». Add the unit with formatSum(n, true) → «4 165 000 сум». */
export function formatSum(n: number, withUnit = false): string {
  const v = formatNumber(Math.round(n));
  return withUnit ? `${v} ${i18n.t('common:units.sum')}` : v;
}

/** Dollars, the design's way: «$1 284», «$0,91», «$12,80». Fractions only when the amount has cents. */
export function formatUsd(n: number, fractionDigits?: number): string {
  const digits = fractionDigits ?? (Number.isInteger(n) ? 0 : 2);
  return `$${formatNumber(n, digits)}`;
}

/** Large sums for KPIs: { value: «128,4», unit: «млн сум» } or { value: «1,54», unit: «млрд сум» }. */
export function formatCompactSum(n: number): { value: string; unit: string } {
  if (Math.abs(n) >= 1e9) return { value: formatNumber(n / 1e9, 2), unit: i18n.t('common:units.billion_sum') };
  if (Math.abs(n) >= 1e6) return { value: formatNumber(n / 1e6, 1), unit: i18n.t('common:units.million_sum') };
  return { value: formatSum(n), unit: i18n.t('common:units.sum') };
}

/** Change against a previous value: «+9,6%». */
export function formatDeltaPct(current: number, previous: number): string {
  if (!previous) return '';
  const pct = ((current - previous) / previous) * 100;
  const sign = pct > 0 ? '+' : pct < 0 ? '−' : '';
  return `${sign}${formatNumber(Math.abs(pct), 1)}%`;
}

function parts(d: Date | string) {
  const date = typeof d === 'string' ? new Date(d.length === 10 ? `${d}T12:00:00+05:00` : d) : d;
  const p = new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (t: string) => p.find((x) => x.type === t)?.value ?? '';
  return { dd: get('day'), mm: get('month'), yy: get('year'), hh: get('hour'), mi: get('minute'), date };
}

/** «01.03.26» (Tashkent time). */
export function formatDateShort(d: Date | string): string {
  const { dd, mm, yy } = parts(d);
  return `${dd}.${mm}.${yy}`;
}

/** «01.03.26 – 28.02.27». */
export function formatDateRange(from: Date | string, to: Date | string): string {
  return `${formatDateShort(from)} – ${formatDateShort(to)}`;
}

/** «14:32» (Tashkent time). */
export function formatTime(d: Date | string): string {
  const { hh, mi } = parts(d);
  return `${hh}:${mi}`;
}

/** «12.2027» from «2027-12». */
export function formatMonthYear(ym: string): string {
  const [y, m] = ym.split('-');
  return `${m}.${y}`;
}

/** The month name (nominative, lower case): «октябрь» for «2026-10». */
export function monthName(ym: string, form: 'nominative' | 'dative' = 'nominative'): string {
  const list = i18n.t(form === 'dative' ? 'common:months_dative' : 'common:months', { returnObjects: true }) as string[];
  return list[Number(ym.slice(5, 7)) - 1] ?? '';
}

function dayStart(d: Date): number {
  const { dd, mm, yy } = parts(d);
  return Date.UTC(2000 + Number(yy), Number(mm) - 1, Number(dd));
}

/**
 * Relative time, short: «сейчас», «12 мин», «2 ч», «вчера», «2 дня», «неделю», then the date.
 * `ago` adds «назад» where the design does («12 мин назад»; «вчера» stays as is).
 */
export function formatRelative(at: Date | string, nowDate: Date, ago = false): string {
  const date = typeof at === 'string' ? new Date(at) : at;
  const mins = Math.floor((nowDate.getTime() - date.getTime()) / 60000);
  const t = (k: string, o?: Record<string, unknown>) => i18n.t(`common:relative.${k}` as 'common:relative.now', o ?? {});
  const withAgo = (s: string) => (ago ? t('ago', { when: s }) : s);
  if (mins < 1) return t('now');
  if (mins < 60) return withAgo(t('minutes', { count: mins }));
  const days = Math.round((dayStart(nowDate) - dayStart(date)) / 86400000);
  if (days <= 0) return withAgo(t('hours', { count: Math.floor(mins / 60) }));
  if (days === 1) return t('yesterday');
  if (days < 7) return withAgo(t('days', { count: days }));
  if (days < 14) return withAgo(t('week'));
  const month = (i18n.t('common:months', { returnObjects: true }) as string[])[date.getMonth()] ?? '';
  return month;
}

/** Whole days from `nowDate` until the date (dates are compared in Tashkent). */
export function daysUntil(d: Date | string, nowDate: Date): number {
  const target = typeof d === 'string' ? new Date(d.length === 10 ? `${d}T12:00:00+05:00` : d) : d;
  return Math.round((dayStart(target) - dayStart(nowDate)) / 86400000);
}

/** «Дилноза Каримова» → «Д. Каримова» (the design's short form in tables and tags). */
export function shortName(full: string): string {
  const parts = full.trim().split(/\s+/);
  if (parts.length < 2) return full;
  const [first, ...rest] = parts;
  return `${first?.[0] ?? ''}. ${rest.join(' ')}`;
}

/** «305482117» → «305 482 117». */
export function formatTin(tin: string): string {
  return tin.replace(/\D/g, '').replace(/(\d{3})(?=\d)/g, '$1 ');
}
